"use client";

import React from "react";
import { Info, Loader2, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { deleteDocument } from "@/api/simpego";

interface ConsultationProtocolUploadProps {
  contractId: string | null;
  contractToken: string | null;
  /** The loaded offer is being copied, so its document lives on another contract and must not be deleted remotely. */
  isCopyMode: boolean;
  documentId: number | null;
  filename: string | null;
  pendingFile: string | null;
  pendingFilename: string | null;
  onSetPending: (base64File: string, filename: string) => void;
  onClearDocument: () => void;
  onClearPending: () => void;
}

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("File read failed"));
    reader.onload = () => {
      if (typeof reader.result !== "string") {
        reject(new Error("Invalid file data"));
        return;
      }
      const base64 = reader.result.split(",")[1];
      if (!base64) {
        reject(new Error("Empty file data"));
        return;
      }
      resolve(base64);
    };
    reader.readAsDataURL(file);
  });

/** Upload of the Beratungsprotokoll / Kundenmandat (rendered for logged-in agents only). The file is only kept in the funnel state here; it is
 * sent to the backend together with the offer, when the personal data are submitted. */
const ConsultationProtocolUpload: React.FC<ConsultationProtocolUploadProps> = ({
  contractId,
  contractToken,
  isCopyMode,
  documentId,
  filename,
  pendingFile,
  pendingFilename,
  onSetPending,
  onClearDocument,
  onClearPending,
}) => {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = "";
    if (!["application/pdf", "image/jpeg", "image/png"].includes(file.type)) {
      setError(t("consultationProtocol.errors.type"));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError(t("consultationProtocol.errors.size"));
      return;
    }
    if (documentId) {
      setError(t("consultationProtocol.deleteFirst"));
      return;
    }
    setError(null);
    try {
      onSetPending(await fileToBase64(file), file.name);
    } catch (err) {
      console.error("Document read failed:", err);
      setError(t("consultationProtocol.errors.readFailed"));
    }
  };

  const handleDelete = async () => {
    setError(null);
    if (documentId && contractId && contractToken && !isCopyMode) {
      setIsDeleting(true);
      try {
        await deleteDocument(contractToken, contractId, documentId);
      } catch (err) {
        console.error("Document delete failed:", err);
        setError(t("consultationProtocol.errors.deleteFailed"));
        setIsDeleting(false);
        return;
      }
      setIsDeleting(false);
    }
    onClearDocument();
    onClearPending();
  };

  const hasFile = !!(documentId || pendingFile);
  const shownName = (documentId ? filename : pendingFilename) || t("consultationProtocol.fallbackFilename");

  return (
    <div className="pt-4 border-t">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <h3 className="text-xl font-semibold">{t("consultationProtocol.input")}</h3>
          <Dialog>
            <DialogTrigger asChild>
              <Button type="button" variant="ghost" size="icon" className="h-6 w-6">
                <Info className="h-5! w-5! text-muted-foreground" />
                <span className="sr-only">{t("common.info")}</span>
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t("consultationProtocol.input")}</DialogTitle>
                <DialogDescription className="whitespace-pre-line">{t("consultationProtocol.tooltip")}</DialogDescription>
              </DialogHeader>
            </DialogContent>
          </Dialog>
        </div>
        <div className="w-full space-y-1 sm:max-w-xl">
          {hasFile && <div className="rounded-full border border-border/60 bg-background px-4 py-2 text-sm">{shownName}</div>}
          <div className="flex w-full flex-wrap items-center justify-between gap-1.5">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 px-3 text-xs"
              onClick={() => inputRef.current?.click()}
            >
              {t("consultationProtocol.upload")}
            </Button>
            {hasFile && (
              <Button type="button" variant="outline" size="sm" className="h-8 px-3 text-xs" onClick={handleDelete} disabled={isDeleting}>
                {isDeleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <X className="h-4 w-4" aria-hidden="true" />}
                {t(documentId ? "consultationProtocol.delete" : "consultationProtocol.clear")}
              </Button>
            )}
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
      </div>
      <input ref={inputRef} type="file" accept="application/pdf,image/jpeg,image/png" className="hidden" onChange={handleUpload} />
    </div>
  );
};

export default ConsultationProtocolUpload;
